import axios from "axios";
import ReusableTable from "../ReusableTable/ReusableTable";
import classes from "./Members.module.css";
import { env } from "../../constants/env";
import { useEffect, useState } from "react";
import CommonButton from "../ui/CommonButton/CommonButton";
import InputField from "../ui/InputField/InputField";
import OverlayModal from "../ui/OverlayModal/OverlayModal";
import LabSelector from "./components/LabSelector";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";

const Members = () => {
  const { token, user } = useAuth(); // user includes role
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [labs, setLabs] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    fullName: "",
    email: "",
    role: "",
    password: "",
    labs: [],
    labId: "",
  });

  const userColumns = [
    { label: "Name", field: "fullName" },
    { label: "Email", field: "email" },
    { label: "Join Date", field: "joinDate" },
    { label: "Role", field: "role" },
  ];

  // role options (added HEALTH CENTER)
  const roleOptions = [
    { label: "Assessor", value: "ASSESSOR" },
    { label: "Ministry", value: "MINISTRY" },
    { label: "Owner", value: "OWNER" },
    { label: "Laboratory", value: "LABORATORY" },
    { label: "Health Center", value: "HEALTH CENTER" }, // ✅ NEW ROLE
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${env.kataApiUrl}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const formattedData = res.data.map((user) => ({
        id: user._id,
        username: user.username,
        fullName: user.fullName,
        email: user.email,
        joinDate: new Date(user.createdAt).toLocaleDateString(),
        labs: user.accessibleLabs || [],
        role: user.role,
        labId: user.labId || null,
      }));
      setUserData(formattedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    try {
      const res = await axios.get(`${env.kataApiUrl}/api/labs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabs(res.data.map((lab) => ({ label: lab.labName, value: lab._id })));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLabs();
  }, []);

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      fullName: "",
      email: "",
      role: "",
      password: "",
      labs: [],
      labId: "",
    });
    setModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setSelectedLabs(user.labs || []);
    setFormData({
      username: user.username,
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      labs: user.labs || [],
      labId: user.labId || "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      // Prevent MINISTRY from creating OWNER
      if (user.role === "MINISTRY" && formData.role === "OWNER") {
        toast.error("MINISTRY users cannot create OWNER accounts");
        setSubmitLoading(false);
        return;
      }

      const payload = { ...formData, labs: selectedLabs };
      if (!editingUser) payload.password = formData.password;

      if (editingUser) {
        await axios.put(
          `${env.kataApiUrl}/api/users/${editingUser.id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Member updated successfully!");
      } else {
        await axios.post(`${env.kataApiUrl}/api/users`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Member created successfully!");
      }

      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Something went wrong!");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.fullName}?`))
      return;
    try {
      await axios.delete(`${env.kataApiUrl}/api/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Member deleted successfully!");
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete member");
    }
  };

  return (
    <div className={classes.wrapper}>
      <ReusableTable
        title="Members"
        columns={userColumns}
        data={userData}
        loading={loading}
        onAdd={handleAdd}
        showTopAdd={userData.length > 0}
        showDetails={false}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {modalOpen && (
        <OverlayModal
          title={editingUser ? "Edit Member" : "Create Member"}
          onClose={() => setModalOpen(false)}
        >
          <form
            onSubmit={handleSubmit}
            className="d-flex flex-column gap-3 p-3"
          >
            <InputField
              labelText="UserName"
              placeholder="Enter UserName"
              name="username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
            />
            <InputField
              labelText="Full Name"
              placeholder="Enter full name"
              name="fullName"
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
            />
            <InputField
              labelText="Email"
              placeholder="Enter email address"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
            {!editingUser && (
              <InputField
                labelText="Password"
                placeholder="Enter password"
                name="password"
                type="password"
                value={formData.password || ""}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                showEye
              />
            )}

            <InputField
              labelText="Role"
              placeholder="Select a role"
              name="role"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              dropdownOptions={
                user.role === "MINISTRY"
                  ? roleOptions.filter((opt) => opt.value !== "OWNER")
                  : roleOptions
              }
            />

            {/* Show Lab dropdown for LABORATORY and HEALTH CENTER users */}
            {(formData.role === "LABORATORY" ||
              formData.role === "HEALTH CENTER") && (
              <InputField
                labelText="Lab"
                placeholder="Select a lab"
                name="labId"
                value={formData.labId}
                onChange={(e) =>
                  setFormData({ ...formData, labId: e.target.value })
                }
                dropdownOptions={labs}
              />
            )}

            {formData.role === "ASSESSOR" && (
              <LabSelector
                labs={labs}
                selectedLabs={selectedLabs}
                setSelectedLabs={setSelectedLabs}
              />
            )}

            <CommonButton
              type="submit"
              title={
                submitLoading
                  ? "Please wait..."
                  : editingUser
                  ? "Update Member"
                  : "Create Member"
              }
              className="btn btn-primary"
              disabled={submitLoading}
            />
          </form>
        </OverlayModal>
      )}
    </div>
  );
};

export default Members;
